import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIntlWrapperContext } from '../../Helpers/IntlWrapperProvider';
import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';
import useObjectHelper from '../../Helpers/useObjectHelper';
import useStickyState from '../../Helpers/useStickyState';
import {
	positions,
	researchFields,
} from '../../Helpers/StaticData';

import {
	CardData,
	FilteringTypes,
	Filters,
	TabStatus,
	UsersResponse
} from './MentorsMenteesList.model';

import { Field } from '../../components/FiltersForm/FiltersForm.model';
import { useAuthorizationDataContext } from "../../Helpers/AuthorizationDataProvider";

export const pageSize = 24;

const useMentorsMenteesList = () => {
	const [activeFilters, setActiveFilters] = useState<Filters | null>(null);
	const [tabStatus, setTabStatus] = useState(TabStatus.Loading);
	const [pagination, setPagination] = useState({ page: 1, size: pageSize, total: 0 });
	const [stickyFilters, setStickyFilters] = useStickyState<Filters>({ bookmarkedOnly: false }, 'stickyFilters');
	const [users, setUsers] = useState<CardData[]>([]);
	const [showMobileFilters, setShowMobileFilters] = useState(false);

	const { get, post, remove } = useFetchWrapper();

	const { arrToOptionList, cleanObj, objToOptionList } = useObjectHelper();

	const intl = useIntl();

	const { i18nLanguages } = useIntlWrapperContext();

	const { role, isAuthorized } = useAuthorizationDataContext();

	useEffect(() => {
		// Skip useless initial load
		pagination.size > pageSize && getUsers(activeFilters);
	}, [pagination.size]);

	useEffect(() => {
		if (activeFilters) {
			setTabStatus(TabStatus.Loading);
			getUsers(activeFilters);
		}
	}, [activeFilters]);

	useEffect(() => {
		!activeFilters && setActiveFilters(stickyFilters);
	}, [stickyFilters]);

	const filterDefaultValues: Record<FilteringTypes, any> = {
		currentPosition: undefined,
		institution: undefined,
		isReachableByCall: false,
		isReachableByText: false,
		languages: [],
		researchAreas: [],
		role: undefined
	};

	const filterStickyValues: Record<FilteringTypes, any> = {
		currentPosition: stickyFilters.currentPosition,
		institution: stickyFilters.institution,
		isReachableByCall: stickyFilters.isReachableByCall,
		isReachableByText: stickyFilters.isReachableByText,
		languages: stickyFilters.languages ? [...stickyFilters.languages] : [],
		researchAreas: stickyFilters.researchAreas ? [...stickyFilters.researchAreas] : [],
		role: stickyFilters.role
	};

	const filterFields: Field<Record<FilteringTypes, any>>[] = [
		{
			label: intl.formatMessage({ id: 'formLabels.userType' }),
			name: 'role',
			options: { optionList: arrToOptionList( isAuthorized(['ViewApplicantMentees']) ? ['Mentor', 'Mentee', 'ApplicantMentee'] : role !== "ApplicantMentee" ? ['Mentor', 'Mentee'] : ["Mentor"]) },
			placeholder: intl.formatMessage({ id: 'formPlaceholders.userType' }),
			type: 'select',
		}, {
			name: 'currentPosition',
			options: { optionList: arrToOptionList(positions) },
			type: 'select',
		}, {
			name: 'institution',
			type: 'input',
		}, {
			name: 'researchAreas',
			options: { optionList: arrToOptionList(researchFields) },
			type: 'multiple',
		}, {
			name: 'languages',
			options: { optionList: objToOptionList(i18nLanguages), type: 'codeBased' },
			type: 'multiple',
		}, {
			name: 'isReachableByText',
			type: 'checkbox',
		}, {
			name: 'isReachableByCall',
			type: 'checkbox',
		}
	];

	const bookmarkUser = async (userId: string) => {
		await post(`/user/bookmark/${userId}`, {});
		getUsers(activeFilters);
	}

	const deleteBookmark = async (bookmarkId: string | undefined) => {
		await remove(`/user/bookmark/${bookmarkId}`);
		getUsers(activeFilters);
	}

	const getUsers = async (filters: Filters | null = {}) => {
		setTabStatus(TabStatus.Loading);
		const queryFilters = filters && Object.keys(filters).map(key => `${key}=${filters[key]}`).join('&');
		const res: HttpResponse<UsersResponse[]> = await get(`/user?page=${pagination.page}&size=${pagination.size}&${queryFilters}`);

			if (res.parsedBody && res.status === 200) {
				const users = res.parsedBody;

				setUsers(users.map((item, index) => ({
					bookmarkId: item.bookmarkId,
					education: item.education,
					id: item.userId,
					imageId: item.imageId,
					key: `user${index}`,
					location: item.location,
					name: `${item.firstName} ${item.lastName}`,
					researchAreas: item.researchAreas,
					userType: item.role,
					workExperience: item.workExperience,
				})) || []);

				setTabStatus(TabStatus.Loaded);
			}
			else {
				setTabStatus(TabStatus.Error);
			}
	}

	const onChangeViewBookmarkedOnly = (e: any) => {
		const filters: Filters = {
			...activeFilters,
			bookmarkedOnly: e.target.checked
		};

		setActiveFilters(filters);
	}

	const onResetFilters = async (callBack?: () => void) => {
		const filters: Filters = {
			bookmarkedOnly: activeFilters?.bookmarkedOnly || false,
			name: activeFilters?.name || '',
		}

		setActiveFilters(filters);

		setStickyFilters({
			bookmarkedOnly: activeFilters?.bookmarkedOnly || false
		});

		callBack && callBack();
	}

	const onSearchByTerm = async (value: string) => {
		const filters: Filters = {
			...activeFilters,
			name: value,
		};

		setActiveFilters(filters);
	}

	const onSubmitFilters = async (data: Filters, callBack?: () => void) => {

		const filters: Filters = {
			bookmarkedOnly: activeFilters?.bookmarkedOnly || false,
			name: activeFilters?.name || '',
			...cleanObj(data)
		};

		setActiveFilters(filters);

		callBack && callBack();
	}

	const onSaveSearchResults = () => {
		setStickyFilters(activeFilters);
	}

	const toggleMobileFilters = () => {
		setShowMobileFilters((currentValue) => !currentValue);
	}

	const onChangePagination = () => {

		if (pagination.size === users.length) {
			setPagination((currentValue) => ({ ...pagination, size: currentValue.size + pageSize, }));
		}
	}

	const onRefresh = () => {
		setTabStatus(TabStatus.Loading);
		getUsers(activeFilters);
	}

	return {
		bookmarkUser,
		deleteBookmark,
		onChangePagination,
		onChangeViewBookmarkedOnly,
		onRefresh,
		onResetFilters,
		onSaveSearchResults,
		onSearchByTerm,
		onSubmitFilters,
		state: {
			filterDefaultValues,
			filterFields,
			filterStickyValues,
			pagination,
			showMobileFilters,
			stickyFilters,
			tabStatus,
			users
		},
		toggleMobileFilters
	};
}

export default useMentorsMenteesList;